import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import ApiService from "../../services/ApiService";
import { formatCurrency, formatNumber } from "../../utils/formatUtils";

const useMarketAnalysis = (sponsorObjectId) => {
  const { t } = useTranslation();
  const tenant = useSelector((state) => state.auth.tenant);
  const date = useSelector((state) => state.date);
  const selectedCurrency = useSelector((state) => state.currency.selectedCurrency);

  const { startDate, endDate } = date;
  const enabled = !!(date.startDate && date.endDate);
  let since;
  let until;
  if (enabled) {
    since = format(date.startDate, "yyyy-MM-dd");
    until = format(date.endDate, "yyyy-MM-dd");
  }

  return useQuery({
    queryKey: [
      "MARKET_ANALYSIS",
      sponsorObjectId,
      tenant.id,
      startDate,
      endDate,
    ],
    queryFn: () =>
      ApiService.getMarketAnalysis(since, until, sponsorObjectId),
    select: (data) => {
      const analysisData = data.data;
      
      if (!analysisData.data || analysisData.data.length === 0) {
        return {
          headers: [
            { Header: t("Market"), accessor: "market" },
            { Header: t("Impressions"), accessor: "impressions" },
            { Header: t("Exposures"), accessor: "exposures" },
            { Header: t("Media Value"), accessor: "media_value" },
            { Header: t("% of Total"), accessor: "percentage" },
          ],
          rows: [],
        };
      }

      const rows = analysisData.data.map((row) => ({
        market: row.market_name || row.country_name || t("Unknown"),
        impressions: formatNumber(row.impressions || 0, selectedCurrency, "0,0"),
        exposures: formatNumber(row.exposures || 0, selectedCurrency, "0,0"),
        media_value: formatCurrency(row.media_value || 0, selectedCurrency),
        percentage: `${(row.percentage || 0).toFixed(1)}%`,
      }));

      return {
        headers: [
          { Header: t("Market"), accessor: "market" },
          { Header: t("Impressions"), accessor: "impressions" },
          { Header: t("Exposures"), accessor: "exposures" },
          { Header: t("Media Value"), accessor: "media_value" },
          { Header: t("% of Total"), accessor: "percentage" },
        ],
        rows,
      };
    },
    enabled,
    placeholderData: { 
      data: { 
        headers: [
          { Header: t("Market"), accessor: "market" },
          { Header: t("Impressions"), accessor: "impressions" },
          { Header: t("Exposures"), accessor: "exposures" },
          { Header: t("Media Value"), accessor: "media_value" },
          { Header: t("% of Total"), accessor: "percentage" },
        ], 
        rows: [],
      } 
    },
  });
};

export default useMarketAnalysis;